// Fork: curated status page components (System Settings → Status Page).
import { useEffect, useState, type FormEvent } from 'react'
import { Plus, Save, Trash2 } from 'lucide-react'
import {
  createStatusComponent,
  deleteStatusComponent,
  listStatusComponents,
  setStatusComponentMatchLabel,
  updateStatusComponent,
  type ListedStatusComponent,
  type StatusComponentInput,
} from '../api/statusComponents'
import { Button } from './ui/Button'

const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg border border-border-primary bg-surface-secondary text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary'
const EMPTY: StatusComponentInput = { key: '', name: '', description: '', position: 0 }

function errorMessage(failure: unknown, fallback: string): string {
  return failure instanceof Error ? failure.message : fallback
}

function ComponentForm({ initial, submitLabel, idPrefix, onSubmit, onCancel }: {
  initial: StatusComponentInput
  submitLabel: string
  idPrefix: string
  onSubmit: (input: StatusComponentInput) => Promise<void>
  onCancel?: () => void
}) {
  const [input, setInput] = useState(initial)
  const [saving, setSaving] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSaving(true)
    try {
      await onSubmit({ ...input, key: input.key.trim(), name: input.name.trim(), description: input.description.trim() })
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_1fr_6rem]">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="block text-xs font-medium text-text-secondary mb-1">Name</label>
        <input id={`${idPrefix}-name`} required maxLength={200} value={input.name} onChange={e => setInput({ ...input, name: e.target.value })} placeholder="Public API" className={INPUT_CLASS} />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-key`} className="block text-xs font-medium text-text-secondary mb-1">Key</label>
        <input id={`${idPrefix}-key`} required maxLength={100} pattern="[a-z0-9][a-z0-9._\-]*" value={input.key} onChange={e => setInput({ ...input, key: e.target.value })} placeholder="api" className={`${INPUT_CLASS} font-mono`} />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-position`} className="block text-xs font-medium text-text-secondary mb-1">Order</label>
        <input id={`${idPrefix}-position`} type="number" value={input.position} onChange={e => setInput({ ...input, position: Number(e.target.value) || 0 })} className={INPUT_CLASS} />
      </div>
      <div className="sm:col-span-3">
        <label htmlFor={`${idPrefix}-description`} className="block text-xs font-medium text-text-secondary mb-1">Description (optional)</label>
        <input id={`${idPrefix}-description`} maxLength={500} value={input.description} onChange={e => setInput({ ...input, description: e.target.value })} className={INPUT_CLASS} />
      </div>
      <div className="sm:col-span-3 flex gap-2">
        <Button type="submit" size="sm" loading={saving}>{submitLabel}</Button>
        {onCancel && <Button type="button" size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>}
      </div>
    </form>
  )
}

function matchedAgo(iso: string): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 3_600_000
  if (hours < 1) return 'less than an hour ago'
  if (hours < 48) return `${Math.round(hours)} hours ago`
  return `${Math.round(hours / 24)} days ago`
}

function MatchStatus({ label, componentKey, lastMatchedAt }: { label: string; componentKey: string; lastMatchedAt: string | null }) {
  if (lastMatchedAt) {
    return <p className="text-xs text-text-tertiary mt-0.5">Last incident with {label}={componentKey}: {matchedAgo(lastMatchedAt)}</p>
  }
  return (
    <p className="text-xs text-amber-700 mt-0.5">
      No incident has had {label}={componentKey} in the last 90 days. Check the key matches a service name, or link incidents manually.
    </p>
  )
}

export function StatusComponentsSettings() {
  const [components, setComponents] = useState<ListedStatusComponent[] | null>(null)
  const [savedMatchLabel, setSavedMatchLabel] = useState('')
  const [matchLabel, setMatchLabel] = useState('')
  const [savingLabel, setSavingLabel] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  async function reload() {
    const result = await listStatusComponents()
    setComponents(result.components)
    setSavedMatchLabel(result.match_label)
    return result
  }

  useEffect(() => {
    listStatusComponents()
      .then(result => {
        setComponents(result.components)
        setMatchLabel(result.match_label)
        setSavedMatchLabel(result.match_label)
      })
      .catch(failure => setError(errorMessage(failure, 'Failed to load status components')))
  }, [])

  async function run(action: () => Promise<unknown>, success: string, fallback: string): Promise<void> {
    setError('')
    setNotice('')
    try {
      await action()
      await reload()
      setNotice(success)
    } catch (failure) {
      setError(errorMessage(failure, fallback))
      throw failure
    }
  }

  async function saveLabel(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSavingLabel(true)
    await run(() => setStatusComponentMatchLabel(matchLabel.trim()), 'Label matching saved', 'Failed to save label').catch(() => {})
    setSavingLabel(false)
  }

  return (
    <section className="bg-surface-primary border border-border-primary rounded-xl p-6 space-y-5" aria-labelledby="status-components-title">
      <div>
        <h2 id="status-components-title" className="text-base font-semibold text-text-primary">Status Page Components</h2>
        <p className="text-sm text-text-secondary mt-0.5">
          Services shown on the status page with a 90-day uptime history. History comes only from published incidents: critical and high incidents count as outages, medium and low as degraded.
        </p>
      </div>

      <form onSubmit={saveLabel} className="space-y-1">
        <label htmlFor="status-components-label" className="block text-sm font-medium text-text-primary">Match incidents by label</label>
        <div className="flex gap-2">
          <input id="status-components-label" value={matchLabel} onChange={e => setMatchLabel(e.target.value)} maxLength={100} placeholder="service" aria-describedby="status-components-label-help" className={`${INPUT_CLASS} font-mono`} />
          <Button type="submit" size="sm" loading={savingLabel}><Save className="w-4 h-4" />Save</Button>
        </div>
        <p id="status-components-label-help" className="text-xs text-text-tertiary">
          A published incident whose label of this name equals a component key counts against that component automatically. Leave blank to link incidents manually only.
        </p>
      </form>

      {components === null && !error && <p className="text-sm text-text-secondary">Loading…</p>}
      {components && components.length === 0 && !adding && (
        <p className="text-sm text-text-secondary">No components yet. The status page shows incidents only.</p>
      )}
      {components && components.length > 0 && (
        <ul className="divide-y divide-border-primary border border-border-primary rounded-lg">
          {components.map(component => (
            <li key={component.id} className="p-3">
              {editing === component.id ? (
                <ComponentForm
                  idPrefix={`status-component-${component.id}`}
                  initial={{ key: component.key, name: component.name, description: component.description, position: component.position }}
                  submitLabel="Save component"
                  onCancel={() => setEditing(null)}
                  onSubmit={input => run(() => updateStatusComponent(component.id, input), 'Component saved', 'Failed to save component').then(() => setEditing(null), () => {})}
                />
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-text-primary break-words">{component.name} <span className="font-mono text-xs text-text-tertiary">{component.key}</span></p>
                    {component.description && <p className="text-xs text-text-secondary break-words">{component.description}</p>}
                    {savedMatchLabel && <MatchStatus label={savedMatchLabel} componentKey={component.key} lastMatchedAt={component.last_matched_at} />}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(component.id)}>Edit</Button>
                    <Button
                      type="button" size="sm" variant="ghost" aria-label={`Delete ${component.name}`}
                      onClick={() => {
                        if (window.confirm(`Delete "${component.name}"? Its uptime history is removed from the status page.`)) {
                          run(() => deleteStatusComponent(component.id), 'Component deleted', 'Failed to delete component').catch(() => {})
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {adding ? (
        <ComponentForm
          idPrefix="status-component-new"
          initial={{ ...EMPTY, position: components?.length ?? 0 }}
          submitLabel="Add component"
          onCancel={() => setAdding(false)}
          onSubmit={input => run(() => createStatusComponent(input), 'Component added', 'Failed to add component').then(() => setAdding(false), () => {})}
        />
      ) : (
        <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(true)}><Plus className="w-4 h-4" />Add component</Button>
      )}

      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {notice && <p role="status" className="text-sm text-green-600">{notice}</p>}
    </section>
  )
}
